import { initializeApp } from "firebase/app";

import { getAuth, connectAuthEmulator, signInWithRedirect, signInWithEmailAndPassword, signInWithCredential, signInWithCustomToken, signInWithPopup, GoogleAuthProvider, getRedirectResult, onAuthStateChanged, signOut, sendPasswordResetEmail, deleteUser } from "firebase/auth";

import { getFirestore, initializeFirestore, persistentLocalCache, persistentSingleTabManager, connectFirestoreEmulator, collection, getDocs, doc, addDoc, query, where, updateDoc, deleteDoc, onSnapshot, orderBy, getDoc, setDoc, arrayUnion, Timestamp, enableNetwork, disableNetwork } from "firebase/firestore";

import { getDatabase, onValue, ref, set, push, get, child, update } from "firebase/database";

import { getFunctions, connectFunctionsEmulator, httpsCallable } from 'firebase/functions';

import { getStorage, connectStorageEmulator, ref as storageRef, uploadBytes, getDownloadURL, uploadBytesResumable } from "firebase/storage";

console.log('[Firebase] Starting Firebase configuration');

// Get Firebase config from environment variables
const getFirebaseConfig = () => {
  // In production builds, environment variables are embedded at build time
  const config = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGE_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL
  };

  // Validate that we have the required configuration
  if (!config.apiKey || !config.projectId) {
    console.error('[Firebase] Missing required Firebase configuration:', {
      hasApiKey: !!config.apiKey,
      hasProjectId: !!config.projectId,
      hasAuthDomain: !!config.authDomain
    });
    throw new Error('Firebase configuration is incomplete. Check your environment variables.');
  }

  return config;
};

// Determine if we're in development mode
const isDevelopment = process.env.NODE_ENV === 'development';
const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '0.0.0.0';

console.log('[Firebase] Environment detection:', {
  NODE_ENV: process.env.NODE_ENV,
  isDevelopment: isDevelopment,
  hostname,
  isLocalhost,
});

const firebaseConfig = getFirebaseConfig();

// Log Firebase config (excluding sensitive values)
console.log('[Firebase] Config loaded:', {
  authDomain: firebaseConfig.authDomain,
  projectId: firebaseConfig.projectId,
  storageBucket: firebaseConfig.storageBucket,
  messagingSenderId: firebaseConfig.messagingSenderId,
  databaseURL: firebaseConfig.databaseURL,
  apiKey: firebaseConfig.apiKey ? '***' : undefined,
  appId: firebaseConfig.appId ? '***' : undefined
});

console.log('[Firebase] Full firebaseConfig:', firebaseConfig);
console.log('[Firebase] navigator.onLine:', typeof navigator !== 'undefined' ? navigator.onLine : 'navigator not available');

// Initialize Firebase
console.log('[Firebase] Initializing Firebase app');
const firebase = initializeApp(firebaseConfig);
console.log('[Firebase] Firebase app initialized');

console.log('[Firebase] Initializing Firebase services');
// Enable Firestore persistence (works in web and Electron renderer)
try {
  initializeFirestore(firebase, {
    localCache: persistentLocalCache({ tabManager: persistentSingleTabManager() })
  });
  console.log('[Firebase] Firestore persistence enabled (persistentLocalCache)');
} catch (e) {
  console.warn('[Firebase] Could not enable Firestore persistence:', e?.message || e);
}
const auth = getAuth(firebase);
const database = getFirestore(firebase);
const realtimeDb = getDatabase(firebase);
const functions = getFunctions(firebase);
const storage = getStorage(firebase);
console.log('[Firebase] Firebase services initialized');

// Configure auth persistence for Electron
if (window.electronAPI) {
  console.log('[Firebase] Configuring auth persistence for Electron');
  try {
    // Use the correct persistence method for Electron
    import('firebase/auth').then(({ setPersistence, browserLocalPersistence }) => {
      setPersistence(auth, browserLocalPersistence)
    .then(() => console.log('[Firebase] Auth persistence set to local'))
    .catch(error => console.error('[Firebase] Error setting auth persistence:', error));
    }).catch(error => {
      console.error('[Firebase] Error importing auth persistence:', error);
    });
  } catch (error) {
    console.error('[Firebase] Error setting auth persistence:', error);
  }
}

// Determine if we should use emulators (explicit opt-in only, and only on localhost)
// Guard against accidental "development" env leaking into production hosts.
const requestedServerLocation = process.env.REACT_APP_SERVER_LOCATION;
const useEmulators = isLocalhost && requestedServerLocation === 'development';

console.log('[Firebase] Emulator detection:', {
  REACT_APP_SERVER_LOCATION: requestedServerLocation,
  useEmulators: useEmulators,
  NODE_ENV: process.env.NODE_ENV,
  isLocalhost,
});

// Check if we're in development mode - if so, use emulators
// For safety, only allow emulator use on localhost even if env is set in prod builds
const finalUseEmulators = isDevelopment || useEmulators;

console.log('[Firebase] Final emulator decision:', {
  finalUseEmulators,
  reason: isDevelopment
    ? 'NODE_ENV=development'
    : (useEmulators ? 'REACT_APP_SERVER_LOCATION=development' : 'production'),
  guardedByLocalhost: useEmulators && !isLocalhost ? 'blocked (not localhost)' : undefined,
});

if (finalUseEmulators) {
  console.log('[Firebase] 🔧 Connecting to local emulators...');
  try {
    connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
    connectFirestoreEmulator(database, 'localhost', 8080);
    connectFunctionsEmulator(functions, 'localhost', 5001);
    connectStorageEmulator(storage, 'localhost', 9199);
    console.log('[Firebase] ✅ Connected to emulators');
  } catch (error) {
    console.warn('[Firebase] ⚠️ Error connecting to emulators (may already be connected):', error.message);
  }
} else {
  console.log('[Firebase] 🌐 Using production Firebase services');
}

// Robust Firestore connectivity handling (Electron + Web)
// Problem observed: when starting offline in Electron, the 'online' event may not
// reliably fire, leaving Firestore disabled. We gate disable -> enable with a flag
// and add a light heartbeat to recover if the event is missed.
if (typeof window !== 'undefined') {
  try {
    let firestoreNetworkDisabled = false;

    const tryEnableIfOnline = async () => {
      if (navigator.onLine && firestoreNetworkDisabled) {
        try {
          await enableNetwork(database);
          firestoreNetworkDisabled = false;
          console.log('[Firebase] Firestore network re-enabled after reconnect');
        } catch (e) {
          console.warn('[Firebase] Failed to re-enable network:', e?.message || e);
        }
      }
    };

    const handleOnline = () => { void tryEnableIfOnline(); };
    const handleOffline = async () => {
      try {
        await disableNetwork(database);
        firestoreNetworkDisabled = true;
        console.log('[Firebase] Firestore network disabled (offline)');
      } catch (e) {
        console.warn('[Firebase] Failed to disable network:', e?.message || e);
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial state: do NOT disable if starting offline; just try enabling when online
    if (navigator.onLine) {
      void tryEnableIfOnline();
    } else {
      firestoreNetworkDisabled = true; // assume disabled until we can enable
      // Heartbeat: if the online event is missed, recover within a few seconds
      clearInterval(window.__fwNetInterval);
      window.__fwNetInterval = setInterval(tryEnableIfOnline, 5000);
    }
  } catch (e) {
    console.warn('[Firebase] Failed to wire connectivity listeners:', e?.message || e);
  }
}

// Configure production-only auth settings in Electron when NOT using emulators
if (!finalUseEmulators && window.electronAPI) {
  console.log('[Firebase] Configuring production endpoints for Electron');
  const authDomain = window.electronAPI?.env?.REACT_APP_FIREBASE_AUTH_DOMAIN || process.env.REACT_APP_FIREBASE_AUTH_DOMAIN;
  if (authDomain) {
    console.log('[Firebase] Setting up auth with domain:', authDomain);
    auth.useDeviceLanguage();
    auth.settings.appVerificationDisabledForTesting = false;
    console.log('[Firebase] Auth settings configured for production');
  } else {
    console.error('[Firebase] No auth domain found in environment variables');
  }
}

// Add auth state change listener for debugging (web mode only)
if (!window.electronAPI) {
  onAuthStateChanged(auth, (user) => {
    if (user) {
      console.log('[Firebase] User signed in:', {
        uid: user.uid,
        email: user.email,
        emailVerified: user.emailVerified,
        isAnonymous: user.isAnonymous
      });
    } else {
      console.log('[Firebase] User signed out');
    }
  });
} else {
  console.log('[Firebase] Skipping auth state listener in Electron mode');
}

console.log('[Firebase] Configuration complete');

// Expose a tiny testing helper to inspect the current auth user from DevTools
if (typeof window !== 'undefined') {
  try {
    window.__testAuth = async () => {
      console.log('[TestAuth] getAuth().currentUser requested');
      const user = auth?.currentUser || null;
      if (!user) return null;
      return {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        isAnonymous: user.isAnonymous,
        emailVerified: user.emailVerified,
        providerData: user.providerData,
      };
    };
    console.log('[TestAuth] Helper attached: window.__testAuth()');
  } catch (e) {
    console.warn('[TestAuth] Failed to attach helper:', e?.message || e);
  }
}

export { 
  firebase, auth, database, ref, onValue, realtimeDb, set, push, get, child, update, deleteUser,
  signInWithRedirect, signInWithCredential, signInWithCustomToken, GoogleAuthProvider, getRedirectResult, onAuthStateChanged, signOut, signInWithEmailAndPassword, sendPasswordResetEmail, signInWithPopup,
  collection, getDocs, doc, addDoc, query, where, updateDoc, deleteDoc, onSnapshot, orderBy, getDoc, setDoc, arrayUnion, Timestamp,
  functions, getFunctions, connectFunctionsEmulator, httpsCallable,
  storageRef, uploadBytes, storage, getDownloadURL, uploadBytesResumable
}